import { EditorState } from 'prosemirror-state';
import type {
  TPositionableTextDocument,
  TPositionedDocumentAST,
  TPositionedDocumentAstNodes,
  TParagraphAST,
  TTextAST
} from '../../lib/PositionableTextDocument';
import { escapeHtml } from '@src/utils/html';

type TProseDocumentHtmlRenderMap = Map<
  string,
  TProseDocumentHtmlRenderMap | string
>;

export function stateToPositionedDocumentAstNodes(
  state: EditorState
): TPositionedDocumentAstNodes {
  const pNodes = [];

  state.doc.forEach((p) => pNodes.push(p));

  return pNodes.map((p) => {
    const textNodes = [];
    p.forEach((node) => textNodes.push(node));

    return {
      type: 'p',
      a: new Map([
        ['text-align', p.attrs['text-align']],
        ['data-marks', escapeHtml(JSON.stringify(p.attrs['marks']))]
      ]),
      c: textNodes.map((tn) => {
        const proseNode = tn;

        return {
          type: 'm',
          a: proseNode.marks.reduce((textNodeAttrs, mark) => {
            const markJson = mark.toJSON();
            const markName = markJson.type;
            const markValue = markJson.attrs[markName];

            textNodeAttrs.set(markName, markValue);

            return textNodeAttrs;
          }, new Map()),
          text: tn.text
        };
      })
    };
  });
}

function positionedDocumentNodesFromAstNodes(
  astNodes: TPositionedDocumentAstNodes
) {
  return astNodes
    .map((node) => {
      return (
        '<' +
        node.type +
        ' ' +
        Array.from(node.a)
          .map(([key, value]) => key + '="' + value + '"')
          .join(' ') +
        '>' +
        node.c
          .map(
            (child) =>
              '<' +
              child.type +
              ' ' +
              Array.from(child.a)
                .map(([key, value]) => key + '="' + value + '"')
                .join(' ') +
              '>' +
              child.text +
              '</' +
              child.type +
              '>'
          )
          .join('') +
        '</' +
        node.type +
        '>'
      );
    })
    .join('');
}

function attributeParser(name, value) {
  return name === 'font-size' ? parseInt(value) : value;
}

function attributesFromOpeningHtmlTag(
  openingTag: string
): Map<string, string | number> {
  return (openingTag.match(/[^\s]+?="[^"]*?"/g) || [])
    .map((attribute): [string, string] => attribute.split('='))
    .reduce((attributes, attribute: [string, string]) => {
      attributes.set(
        attribute[0],
        attributeParser(attribute[0], attribute[1].replace(/^"|"$/g, ''))
      );

      return attributes;
    }, new Map());
}

function positionedDocumentTextNodeToAst(node): TTextAST {
  let openTagPointer = 0;

  while (node[openTagPointer] !== '>' && openTagPointer < node.length) {
    openTagPointer += 1;
  }

  openTagPointer += 1;

  return {
    type: 'm',
    a: attributesFromOpeningHtmlTag(node.slice(0, openTagPointer)),
    text: node.slice(openTagPointer, node.length - 4)
  };
}

function positionedDocumentTextNodesToAst(nodes: string): Array<TTextAST> {
  return nodes
    .replace(/^<m |<\/m>$/g, '')
    .split(/<\/m><m /g)
    .filter((str) => str !== '')
    .map((node, index) => {
      if (index > 0) {
        return '<m ' + node + '</m>';
      } else {
        return /\/m>/.test(node) ? node : node + '</m>';
      }
    })
    .map(positionedDocumentTextNodeToAst);
}

function positionedDocumentParagraphNodeToAst(node: string): TParagraphAST {
  let pointer = 0;

  while (node[pointer] !== '>' && pointer < node.length) {
    pointer += 1;
  }

  pointer += 1;

  return {
    type: 'p',
    a: attributesFromOpeningHtmlTag(node.slice(0, pointer)),
    c: positionedDocumentTextNodesToAst(node.slice(pointer, node.length - 4))
  };
}

function positionedDocumentNodesToAst(doc: string) {
  return doc
    .split(/<\/p><p/g)
    .filter((str) => str !== '')
    .map((node, index) => {
      if (index > 0) {
        return '<p' + node;
      } else {
        return /\/p>/.test(node) ? node : node + '</p>';
      }
    })
    .map(positionedDocumentParagraphNodeToAst);
}

export function positionedDocumentToAST(
  positionedDoc: TPositionableTextDocument
): TPositionedDocumentAST {
  const ast = {
    n: positionedDocumentNodesToAst(positionedDoc.ds),
    p: positionedDoc.pos
  };

  return ast;
}

export function astToPositionedDocument(
  ast: TPositionedDocumentAST
): TPositionableTextDocument {
  return { ds: positionedDocumentNodesFromAstNodes(ast.n), pos: ast.p };
}

export function astChildNodesToProseDocumentRenderTree(nodes: Array<TTextAST>) {
  const tree = nodes
    .map((node) => {
      return Array.from(node.a)
        .map((keyVal) => `${keyVal[0]}: ${keyVal[1]}`)
        .reverse()
        .reduce((val, key, currentIndex, sourceArray) => [key, val], node.text);
    })
    .reduce((resultTree, sourceNodeBranch, currentIndex, sourceArray) => {
      if (currentIndex === 0) return sourceNodeBranch;

      let treeParent = resultTree;
      let sourceParent = sourceNodeBranch;

      while (treeParent[treeParent.length - 2] === sourceParent[0]) {
        treeParent = treeParent[treeParent.length - 1];
        sourceParent = sourceParent[sourceParent.length - 1];
      }

      treeParent.push(sourceParent[0]);
      treeParent.push(sourceParent[1]);

      return resultTree;
    }, []);

  return tree;
}

function astChildNodesToProseDocumentHTML(nodes) {
  return proseDocumentRenderTreeToProseDocumentHTML(
    astChildNodesToProseDocumentRenderTree(nodes)
  );
}

function proseDocumentRenderTreeToProseDocumentHTML(renderTree) {
  const result = [];
  let keyIndex = 0;

  // if it is a string, it is the node text
  if (typeof renderTree === 'string') return escapeHtml(renderTree);

  while (keyIndex < renderTree.length) {
    const [key, value] = renderTree[keyIndex].split(/: (.+)$/);

    result.push([
      '<m ' +
        key +
        '="' +
        value +
        '">' +
        proseDocumentRenderTreeToProseDocumentHTML(renderTree[keyIndex + 1]) +
        '</m>'
    ]);

    keyIndex += 2;
  }

  return result.join('');
}

export function astToProseDocumentHTML(ast: TPositionedDocumentAST): string {
  return ast.n
    .map((node) => {
      return (
        '<' +
        node.type +
        ' ' +
        Array.from(node.a)
          .map(([key, value]) => `${key}="${value}"`)
          .join(' ') +
        '>' +
        astChildNodesToProseDocumentHTML(node.c) +
        '</' +
        node.type +
        '>'
      );
    })
    .join('');
}
